import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { api } from "../../convex/_generated/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AgentImageUpload } from "@/components/ui/agent-image-upload";
import { ImageDisplay } from "@/components/ui/image-display";
import { PropertyTable } from "./property-table";
import { PropertyFormWrapper } from "./property-form-wrapper";
import { ContactTable } from "./contact-table";
import { Analytics } from "./pages/Analytics";
import { Drafts } from "./pages/Drafts";
import { SettingsPage } from "./pages/Settings";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Building,
  MapPin,
  Users,
  Settings,
  BarChart3,
  PieChart,
  Activity,
  Star,
  Calendar,
  Menu,
  X,
  Plus,
  Search,
  Bell,
  Database,
  TrendingUp,
  FileText,
  Contact,
  Globe,
  AlertTriangle,
  CheckCircle,
  Info,
  Archive,
} from "lucide-react";
import { usePagination } from "@/hooks/use-pagination";
import { BlogTable } from "./blog-table";
import { BlogForm } from "./blog-form";
import { UserManagement } from "./user-management";
import {
  DashboardSidebar,
  SidebarProvider,
  useSidebarContext,
} from "./dashboard-sidebar";
import type { Id } from "../../convex/_generated/dataModel";
import { isPropertiesTabOnlyEmail } from "@/lib/staff-access";
import { SimpleLoader } from "@/components/ui/simple-loader";

type ActiveView =
  | "overview"
  | "properties"
  | "analytics"
  | "contacts"
  | "areas"
  | "agent-profile"
  | "settings"
  | "blogs"
  | "drafts"
  | "users";

const sidebarItems = [
  {
    id: "overview" as ActiveView,
    label: "Overview",
    icon: <BarChart3 className="h-4 w-4" />,
    description: "Dashboard overview",
  },
  {
    id: "properties" as ActiveView,
    label: "Properties",
    icon: <Building className="h-4 w-4" />,
    description: "Manage properties",
  },
  {
    id: "analytics" as ActiveView,
    label: "Analytics",
    icon: <PieChart className="h-4 w-4" />,
    description: "View analytics",
  },
  {
    id: "contacts" as ActiveView,
    label: "Contacts",
    icon: <Users className="h-4 w-4" />,
    description: "Manage contacts",
  },
  {
    id: "areas" as ActiveView,
    label: "Areas",
    icon: <MapPin className="h-4 w-4" />,
    description: "Manage areas",
  },
  {
    id: "agent-profile" as ActiveView,
    label: "Agent Profile",
    icon: <Users className="h-4 w-4" />,
    description: "Agent profile",
  },
  {
    id: "settings" as ActiveView,
    label: "Settings",
    icon: <Settings className="h-4 w-4" />,
    description: "Settings",
  },
  {
    id: "blogs" as ActiveView,
    label: "Blogs",
    icon: <FileText className="h-4 w-4" />,
    description: "Manage blogs",
  },
  {
    id: "drafts" as ActiveView,
    label: "Drafts",
    icon: <Archive className="h-4 w-4" />,
    description: "Property drafts",
  },
  {
    id: "users" as ActiveView,
    label: "Users",
    icon: <Users className="h-4 w-4" />,
    description: "Manage staff accounts",
  },
];

export function Dashboard() {
  const { isLoading: authLoading } = useConvexAuth();

  const [activeView, setActiveView] = useState<ActiveView>("overview");

  // quick action modals
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showAreaForm, setShowAreaForm] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [showComingSoonDialog, setShowComingSoonDialog] = useState(false);

  // blog management state
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState<Id<"blog"> | null>(null);

  // draft management state
  const [resumingDraftId, setResumingDraftId] =
    useState<Id<"property_drafts"> | null>(null);
  const [resumingDraftData, setResumingDraftData] = useState<any>(null);

  // handle keyboard shortcuts
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowSearchDialog(true);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // fetch dashboard data with optimized queries - use smaller limit for performance
  const currentUser = useQuery(api.users.current);
  const userRole = useQuery(api.users.getCurrentUserRole);

  const isDashboardReady =
    !authLoading && currentUser !== undefined && userRole !== undefined;

  // blogmaster restriction: blogmaster can only access blogs; others cannot access blogs
  const isBlogmaster =
    currentUser && currentUser.email === "blogmaster@blirumahproperty.com";

  // some agents only get the properties tab (no overview analytics, etc.)
  const isPropertiesTabOnly =
    currentUser !== null &&
    currentUser !== undefined &&
    isPropertiesTabOnlyEmail(currentUser.email);

  // trainee restriction: trainees can only access overview, properties, and drafts
  const isTrainee = userRole === "trainee";

  // admin check: only admins can access user management
  const isAdmin = userRole === "admin";

  const properties = useQuery(
    api.properties.getAllBasePropertiesWithTypes,
    isPropertiesTabOnly
      ? "skip"
      : {
          paginationOpts: {
            numItems: 100, // reduced for better performance, PropertyTable will handle its own pagination
            cursor: null,
          },
          filters: {}, // show all properties including hidden and drafts
        },
  );

  const areas = useQuery(api.area.list, isPropertiesTabOnly ? "skip" : {});
  const contacts = useQuery(
    api.contacts.getAllContacts,
    isPropertiesTabOnly
      ? "skip"
      : {
          limit: 1000,
          filters: {},
        },
  );

  // initialize view based on user role (only after user + role queries have settled)
  React.useEffect(() => {
    if (!isDashboardReady || !currentUser) {
      return;
    }

    if (isBlogmaster) {
      setActiveView("blogs");
    } else if (isPropertiesTabOnly && activeView !== "properties") {
      setActiveView("properties");
    } else if (
      isTrainee &&
      activeView !== "overview" &&
      activeView !== "properties" &&
      activeView !== "contacts" &&
      activeView !== "drafts"
    ) {
      setActiveView("overview");
    }
  }, [
    isDashboardReady,
    currentUser,
    isBlogmaster,
    isPropertiesTabOnly,
    isTrainee,
    activeView,
  ]);

  // get proper property statistics (independent of pagination)
  const propertyStats = useQuery(
    api.properties.getPropertyStats,
    isPropertiesTabOnly ? "skip" : {},
  );

  // format stats for compatibility with existing components
  const stats = React.useMemo(() => {
    if (!propertyStats) return null;

    return {
      total: propertyStats.total,
      featured: propertyStats.featured,
      draft: propertyStats.drafts,
      published: propertyStats.published,
    };
  }, [propertyStats]);

  const handleQuickActionSuccess = () => {
    // trigger a re-render by updating a key or use query invalidation
    // for now, we'll just close the modal and let the user manually refresh
    // in a real app, you'd want to use query invalidation or optimistic updates
  };

  const renderContent = () => {
    // blogmaster can only access blogs view
    if (isBlogmaster && activeView !== "blogs") {
      return (
        <div className="w-full max-w-none space-y-6 overflow-y-auto">
          <div className="text-center py-8">
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="text-muted-foreground">
              You can only access the Blogs section.
            </p>
          </div>
        </div>
      );
    }

    if (isPropertiesTabOnly && activeView !== "properties") {
      return (
        <div className="w-full max-w-none space-y-6 overflow-y-auto">
          <div className="text-center py-8">
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="text-muted-foreground">
              You can only access the Properties section.
            </p>
          </div>
        </div>
      );
    }

    // trainee can only access overview, properties, contacts, and drafts
    if (
      isTrainee &&
      activeView !== "overview" &&
      activeView !== "properties" &&
      activeView !== "contacts" &&
      activeView !== "drafts"
    ) {
      return (
        <div className="w-full max-w-none space-y-6 overflow-y-auto">
          <div className="text-center py-8">
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="text-muted-foreground">
              You can only access Overview, Properties, Contacts, and Drafts
              sections.
            </p>
          </div>
        </div>
      );
    }

    switch (activeView) {
      case "overview":
        return (
          <OverviewContent
            stats={stats}
            properties={properties}
            areas={areas}
            contacts={contacts}
            onAddProperty={() => setShowPropertyForm(true)}
            onAddContact={() => setShowContactForm(true)}
            onAddArea={() => setShowAreaForm(true)}
            onGenerateReport={() => setShowReportDialog(true)}
            isTrainee={isTrainee}
          />
        );
      case "properties":
        return <PropertiesContent />;
      case "analytics":
        return <AnalyticsContent />;
      case "contacts":
        return <ContactsContent contacts={contacts} />;
      case "areas":
        return (
          <AreasContent areas={areas} onAddArea={() => setShowAreaForm(true)} />
        );
      case "agent-profile":
        return <AgentProfileContent />;
      case "settings":
        return <SettingsPage />;
      case "blogs":
        return (
          <div className="w-full max-w-none space-y-6 overflow-y-auto">
            <BlogTable
              onCreateBlog={() => {
                setEditingBlogId(null);
                setShowBlogForm(true);
              }}
              onEditBlog={(blogId) => {
                setEditingBlogId(blogId);
                setShowBlogForm(true);
              }}
            />
          </div>
        );
      case "drafts":
        return (
          <Drafts
            onResumeDraft={(draftId, draftData) => {
              setResumingDraftId(draftId);
              setResumingDraftData(draftData);
              setShowPropertyForm(true);
            }}
          />
        );
      case "users":
        // admin-only access
        if (!isAdmin) {
          return (
            <div className="w-full max-w-none space-y-6 overflow-y-auto">
              <div className="text-center py-8">
                <h3 className="text-lg font-medium">Access Restricted</h3>
                <p className="text-muted-foreground">
                  Only administrators can access User Management.
                </p>
              </div>
            </div>
          );
        }
        return (
          <div className="w-full max-w-none space-y-6 overflow-y-auto">
            <UserManagement />
          </div>
        );
      default:
        return (
          <OverviewContent
            stats={stats}
            properties={properties}
            areas={areas}
            contacts={contacts}
          />
        );
    }
  };

  if (!isDashboardReady) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col items-center justify-center bg-background">
        <div className="text-center [-webkit-font-smoothing:antialiased] [text-rendering:optimizeLegibility]">
          <SimpleLoader message="Loading your workspace…" />
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden bg-background">
        <DashboardSidebar
          activeView={activeView}
          onViewChange={setActiveView}
          sidebarItems={sidebarItems}
          isBlogmaster={isBlogmaster}
          isTrainee={isTrainee}
          isAdmin={isAdmin}
          isPropertiesTabOnly={isPropertiesTabOnly}
        />

        {/* main content */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* top header */}
          <header className="flex h-12 shrink-0 items-center justify-between border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <div>
                <h2 className="text-sm font-semibold capitalize leading-none">
                  {activeView}
                </h2>
                <p className="hidden text-xs text-muted-foreground sm:block">
                  {
                    sidebarItems.find((item) => item.id === activeView)
                      ?.description
                  }
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
                <Database className="h-3.5 w-3.5" />
                <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 active:scale-95 transition-all duration-150 ease-out cursor-pointer relative group"
                onClick={() => setShowSearchDialog(true)}
              >
                <Search className="h-3.5 w-3.5" />
                <span className="absolute -top-1 -right-1 bg-muted text-muted-foreground text-xs px-1 py-0.5 rounded border opacity-0 group-hover:opacity-100 transition-opacity">
                  ⌘K
                </span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                onClick={() => setShowComingSoonDialog(true)}
              >
                <Bell className="h-3.5 w-3.5" />
              </Button>
            </div>
          </header>

          {/* main content area */}
          <main className="flex-1 overflow-y-auto p-3">
            <div className="w-full max-w-none min-w-0">{renderContent()}</div>
          </main>
        </div>
      </div>

      {/* quick action modals */}
      <PropertyFormWrapper
        open={showPropertyForm}
        onClose={() => {
          setShowPropertyForm(false);
          setResumingDraftId(null);
          setResumingDraftData(null);
        }}
        mode="create"
        draftId={resumingDraftId || undefined}
        draftData={resumingDraftData || undefined}
      />

      <ContactForm
        open={showContactForm}
        onClose={() => setShowContactForm(false)}
        onSuccess={() => {
          setShowContactForm(false);
        }}
      />

      <AreaForm
        open={showAreaForm}
        onClose={() => setShowAreaForm(false)}
        onSuccess={() => {
          setShowAreaForm(false);
        }}
      />

      <ReportDialog
        open={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        stats={stats}
        properties={properties}
        areas={areas}
        contacts={contacts}
      />

      {/* blog form dialog */}
      <Dialog open={showBlogForm} onOpenChange={setShowBlogForm}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 max-w-6xl">
          <BlogForm
            blogId={editingBlogId || undefined}
            onClose={() => {
              setShowBlogForm(false);
              setEditingBlogId(null);
            }}
            onSuccess={() => {
              setShowBlogForm(false);
              setEditingBlogId(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* search dialog */}
      <CommandDialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <CommandInput placeholder="search pages and navigate..." />
        <CommandList>
          <CommandEmpty>no results found.</CommandEmpty>
          <CommandGroup heading="navigation">
            {sidebarItems
              .filter((item) => {
                if (isBlogmaster) {
                  return item.id === "blogs";
                }
                if (isPropertiesTabOnly) {
                  return item.id === "properties";
                }
                if (isTrainee) {
                  return (
                    item.id === "overview" ||
                    item.id === "properties" ||
                    item.id === "contacts" ||
                    item.id === "drafts"
                  );
                }
                if (item.id === "users" && !isAdmin) {
                  return false;
                }
                return item.id !== "blogs";
              })
              .map((item) => (
                <CommandItem
                  key={item.id}
                  onSelect={() => {
                    setActiveView(item.id);
                    setShowSearchDialog(false);
                  }}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </CommandItem>
              ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="quick actions">
            <CommandItem
              onSelect={() => {
                setShowPropertyForm(true);
                setShowSearchDialog(false);
              }}
              className="flex items-center gap-3 cursor-pointer"
            >
              <Plus className="h-4 w-4" />
              <span>add property</span>
            </CommandItem>
            {!isPropertiesTabOnly && (
              <CommandItem
                onSelect={() => {
                  setShowContactForm(true);
                  setShowSearchDialog(false);
                }}
                className="flex items-center gap-3 cursor-pointer"
              >
                <Contact className="h-4 w-4" />
                <span>add contact</span>
              </CommandItem>
            )}
            {!isPropertiesTabOnly && (
              <CommandItem
                onSelect={() => {
                  setShowAreaForm(true);
                  setShowSearchDialog(false);
                }}
                className="flex items-center gap-3 cursor-pointer"
              >
                <MapPin className="h-4 w-4" />
                <span>add area</span>
              </CommandItem>
            )}
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* coming soon dialog */}
      <Dialog
        open={showComingSoonDialog}
        onOpenChange={setShowComingSoonDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              notifications
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
              <Bell className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold">coming soon!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                we're working on bringing you real-time notifications. stay
                tuned!
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}

// ---------------------------------------------------------------------------
// sidebar trigger component
// ---------------------------------------------------------------------------

function SidebarTrigger() {
  const { isMobile, toggle } = useSidebarContext();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className="h-8 w-8 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
    >
      {isMobile ? <Menu className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
    </Button>
  );
}

// overview content component
// quick action form components
function ContactForm({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    country: "",
    address: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createContact = useMutation(api.contacts.createContact);
  const currentUser = useQuery(api.users.current);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d+$/.test(formData.phone)) {
      newErrors.phone = "Phone must contain only numbers";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createContact({
        name: formData.name,
        phone: parseInt(formData.phone),
        email: formData.email || undefined,
        country: formData.country,
        address: formData.address || undefined,
        created_by: currentUser?.email || "system",
      });
      toast.success("Contact created successfully!");
      setFormData({ name: "", phone: "", email: "", country: "", address: "" });
      onSuccess();
    } catch (error) {
      console.error("failed to create contact:", error);
      toast.error("Failed to create contact. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", phone: "", email: "", country: "", address: "" });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, name: e.target.value }));
                if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
              }}
              placeholder="Enter contact name"
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, phone: e.target.value }));
                if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" }));
              }}
              placeholder="Enter phone number"
              className={errors.phone ? "border-red-500" : ""}
            />
            {errors.phone && (
              <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
            )}
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
                if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
              }}
              placeholder="Enter email address"
              className={errors.email ? "border-red-500" : ""}
            />
            {errors.email && (
              <p className="text-sm text-red-500 mt-1">{errors.email}</p>
            )}
          </div>
          <div>
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              value={formData.country}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, country: e.target.value }));
                if (errors.country)
                  setErrors((prev) => ({ ...prev, country: "" }));
              }}
              placeholder="Enter country"
              className={errors.country ? "border-red-500" : ""}
            />
            {errors.country && (
              <p className="text-sm text-red-500 mt-1">{errors.country}</p>
            )}
          </div>
          <div>
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, address: e.target.value }))
              }
              placeholder="Enter address"
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Contact"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({
  open,
  onClose,
  stats,
  properties,
  areas,
  contacts,
}: {
  open: boolean;
  onClose: () => void;
  stats: any;
  properties: any;
  areas: any;
  contacts: any;
}) {
  const generateReport = () => {
    const reportContent = `
STAFF WEB PROPERTY MANAGEMENT REPORT
Generated on: ${new Date().toLocaleDateString()}

=== SUMMARY STATISTICS ===
Total Properties: ${stats?.total || 0}
Published Properties: ${stats?.published || 0}
Draft Properties: ${stats?.draft || 0}
Featured Properties: ${stats?.featured || 0}
Total Areas: ${areas?.length || 0}
Total Contacts: ${contacts?.length || 0}

=== PROPERTY BREAKDOWN ===
Publication Rate: ${stats?.total ? Math.round(((stats?.published || 0) / stats.total) * 100) : 0}%
Featured Rate: ${stats?.total ? Math.round(((stats?.featured || 0) / stats.total) * 100) : 0}%

=== RECENT PROPERTIES ===
${
  properties
    ? (() => {
        const propertiesArray = properties.page || properties;
        return Array.isArray(propertiesArray)
          ? propertiesArray
              .slice(0, 5)
              .map(
                (p: any, i: number) =>
                  `${i + 1}. ${p.baseProperty?.property_name} (${p.baseProperty?.reff_id}) - ${p.baseProperty?.draft ? "Draft" : "Published"}`,
              )
              .join("\n")
          : "No properties found";
      })()
    : "No properties found"
}

=== CONTACT BREAKDOWN ===
${
  contacts
    ? contacts
        .slice(0, 10)
        .map(
          (c: any, i: number) =>
            `${i + 1}. ${c.name} - ${c.country} (${c.phone})`,
        )
        .join("\n")
    : "No contacts found"
}

Report generated by Staff Web Property Management System
    `.trim();

    // create and download the report
    const blob = new Blob([reportContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `property-report-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success("Report generated and downloaded!");
    onClose();
  };

  const printReport = () => {
    const reportWindow = window.open("", "_blank");
    if (reportWindow) {
      reportWindow.document.write(`
        <html>
          <head>
            <title>Property Management Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #333; border-bottom: 2px solid #ccc; }
              h2 { color: #666; margin-top: 20px; }
              .stat { margin: 5px 0; }
              .property-item { margin: 5px 0; padding: 5px; background: #f9f9f9; }
            </style>
          </head>
          <body>
            <h1>Staff Web Property Management Report</h1>
            <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>

            <h2>Summary Statistics</h2>
            <div class="stat">Total Properties: ${stats?.total || 0}</div>
            <div class="stat">Published Properties: ${stats?.published || 0}</div>
            <div class="stat">Draft Properties: ${stats?.draft || 0}</div>
            <div class="stat">Featured Properties: ${stats?.featured || 0}</div>
            <div class="stat">Total Areas: ${areas?.length || 0}</div>
            <div class="stat">Total Contacts: ${contacts?.length || 0}</div>

            <h2>Recent Properties</h2>
            ${
              properties
                ? (() => {
                    const propertiesArray = properties.page || properties;
                    return Array.isArray(propertiesArray)
                      ? propertiesArray
                          .slice(0, 5)
                          .map(
                            (p: any) =>
                              `<div class="property-item">${p.baseProperty?.property_name} (${p.baseProperty?.reff_id}) - ${p.baseProperty?.draft ? "Draft" : "Published"}</div>`,
                          )
                          .join("")
                      : "<p>No properties found</p>";
                  })()
                : "<p>No properties found</p>"
            }
          </body>
        </html>
      `);
      reportWindow.document.close();
      reportWindow.print();
    }
    toast.success("Report sent to printer!");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate Report</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Generate a comprehensive report of your property management data
            including statistics, recent properties, and contact information.
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-2">Current Statistics</h4>
              <div className="text-sm space-y-1">
                <div>
                  Properties: {stats?.total || 0} total ({stats?.published || 0}{" "}
                  published)
                </div>
                <div>Areas: {areas?.length || 0}</div>
                <div>Contacts: {contacts?.length || 0}</div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" variant="outline" onClick={printReport}>
              <FileText className="h-4 w-4 mr-2" />
              Print Report
            </Button>
            <Button type="button" onClick={generateReport}>
              <FileText className="h-4 w-4 mr-2" />
              Download Report
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AreaForm({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    area_name: "",
    country: "",
    province: "",
    parent_id: "" as string | undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createArea = useMutation(api.area.create);
  const areas = useQuery(api.area.list, {});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.area_name.trim()) {
      newErrors.area_name = "Area name is required";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!formData.province.trim()) {
      newErrors.province = "Province is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createArea({
        input: {
          area_name: formData.area_name,
          country: formData.country,
          province: formData.province,
          parent_id: formData.parent_id
            ? (formData.parent_id as any)
            : undefined,
        },
      });
      toast.success("Area created successfully!");
      setFormData({ area_name: "", country: "", province: "", parent_id: "" });
      onSuccess();
    } catch (error) {
      console.error("failed to create area:", error);
      toast.error("Failed to create area. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ area_name: "", country: "", province: "", parent_id: "" });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Area</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <AreaSearchInput
            value={formData.area_name}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, area_name: value }));
              if (errors.area_name)
                setErrors((prev) => ({ ...prev, area_name: "" }));
            }}
            placeholder="Enter area name"
            label="Area Name *"
            error={errors.area_name}
          />
          <div>
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              value={formData.country}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, country: e.target.value }));
                if (errors.country)
                  setErrors((prev) => ({ ...prev, country: "" }));
              }}
              placeholder="Enter country"
              className={errors.country ? "border-red-500" : ""}
            />
            {errors.country && (
              <p className="text-sm text-red-500 mt-1">{errors.country}</p>
            )}
          </div>
          <div>
            <Label htmlFor="province">Province *</Label>
            <Input
              id="province"
              value={formData.province}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, province: e.target.value }));
                if (errors.province)
                  setErrors((prev) => ({ ...prev, province: "" }));
              }}
              placeholder="Enter province"
              className={errors.province ? "border-red-500" : ""}
            />
            {errors.province && (
              <p className="text-sm text-red-500 mt-1">{errors.province}</p>
            )}
          </div>

          <div>
            <Label htmlFor="parent_id">Parent Area (Optional)</Label>
            <select
              id="parent_id"
              value={formData.parent_id}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  parent_id: e.target.value || undefined,
                }));
              }}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <option value="">No parent (Root area)</option>
              {areas?.map((area: any) => (
                <option key={area._id} value={area._id}>
                  {area.area_name} ({area.province}, {area.country})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Select a parent area to create a sub-area. Leave empty for root
              level.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Area"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// comprehensive gaps analysis card component
function GapsCard() {
  const gaps = useQuery(api.properties.findReffIdGaps);

  if (!gaps) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (gaps.totalGaps === 0) {
    return (
      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-700">
            <CheckCircle className="h-5 w-5" />
            REFF ID Sequence Complete
          </CardTitle>
          <p className="text-sm text-green-600">
            No gaps found in property reference IDs
          </p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="text-2xl font-bold text-green-700 mb-2">
              {gaps.totalProperties}
            </div>
            <p className="text-sm text-green-600">
              Properties with sequential REFF IDs
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-700">
          <AlertTriangle className="h-5 w-5" />
          REFF ID Gaps Analysis
        </CardTitle>
        <p className="text-sm text-orange-600">
          {gaps.totalGaps} missing IDs detected across {gaps.gaps.length} prefix
          groups
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-orange-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-700">
                {gaps.totalGaps}
              </div>
              <p className="text-xs text-orange-600">Total Gaps</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-700">
                {gaps.totalProperties}
              </div>
              <p className="text-xs text-orange-600">Total Properties</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-700">
                {gaps.gaps.length}
              </div>
              <p className="text-xs text-orange-600">Prefix Groups</p>
            </div>
          </div>

          {/* Detailed Gap Information */}
          <div className="space-y-3">
            <h4 className="font-medium text-orange-700">
              Gap Details by Prefix Group
            </h4>
            {gaps.gaps.map((gap) => (
              <div
                key={gap.prefix}
                className="p-3 bg-white rounded-lg border border-orange-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="text-orange-700 border-orange-300"
                    >
                      {gap.prefix}
                    </Badge>
                    <span className="text-sm font-medium text-orange-700">
                      {gap.count} gaps
                    </span>
                  </div>
                  <span className="text-xs text-orange-600">
                    Range: {gap.range.min} - {gap.range.max}
                  </span>
                </div>

                {/* Missing IDs Grid */}
                <div className="grid grid-cols-6 md:grid-cols-10 gap-1">
                  {gap.missingIds.map((missingId) => (
                    <div
                      key={missingId}
                      className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded text-center font-mono"
                      title={`Missing: ${gap.prefix}${missingId}`}
                    >
                      {missingId}
                    </div>
                  ))}
                </div>

                {/* Gap Percentage */}
                <div className="mt-2 text-xs text-orange-600">
                  Gap rate:{" "}
                  {Math.round(
                    (gap.count / (gap.range.max - gap.range.min + 1)) * 100,
                  )}
                  %
                </div>
              </div>
            ))}
          </div>

          {/* Recommendations */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
              <Info className="h-4 w-4" />
              Recommendations
            </h4>
            <ul className="text-sm text-blue-600 space-y-1">
              <li>
                • Consider filling gaps to maintain sequential property
                numbering
              </li>
              <li>
                • Review if missing properties were deleted or never created
              </li>
              <li>
                • Use gaps for future property additions to maintain sequence
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OverviewContent({
  stats,
  properties,
  areas,
  contacts,
  onAddProperty,
  onAddContact,
  onAddArea,
  onGenerateReport,
  isTrainee = false,
}: any) {
  const recentProperties = React.useMemo(() => {
    if (!properties) return [];
    // extract page array from paginated response
    const propertiesArray = properties.page || properties;
    if (!Array.isArray(propertiesArray)) return [];
    return propertiesArray
      .sort(
        (a: any, b: any) =>
          (b.baseProperty?.created_at || 0) - (a.baseProperty?.created_at || 0),
      )
      .slice(0, 5);
  }, [properties]);

  const featuredProperties = React.useMemo(() => {
    if (!properties) return [];
    // extract page array from paginated response
    const propertiesArray = properties.page || properties;
    if (!Array.isArray(propertiesArray)) return [];
    return propertiesArray
      .filter((p: any) => p.baseProperty?.featured && !p.baseProperty?.draft)
      .slice(0, 3);
  }, [properties]);

  if (!properties || !areas || !contacts) {
    return (
      <div className="w-full max-w-none space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-3 overflow-y-auto">
      {/* stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 px-3 pt-3">
            <CardTitle className="text-xs font-medium text-blue-600">
              Total Properties
            </CardTitle>
            <Building className="h-3.5 w-3.5 text-blue-600" />
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="text-xl font-bold text-blue-700">
              {stats?.total || 0}
            </div>
            <p className="text-xs text-blue-600 mt-0.5">
              {stats?.published || 0} published, {stats?.draft || 0} drafts
            </p>
            <div className="mt-1.5">
              <Progress
                value={
                  stats?.total
                    ? ((stats?.published || 0) / stats.total) * 100
                    : 0
                }
                className="h-1"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 px-3 pt-3">
            <CardTitle className="text-xs font-medium text-yellow-600">
              Featured
            </CardTitle>
            <Star className="h-3.5 w-3.5 text-yellow-600" />
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="text-xl font-bold text-yellow-700">
              {stats?.featured || 0}
            </div>
            <p className="text-xs text-yellow-600 mt-0.5">
              {stats?.total
                ? Math.round(((stats?.featured || 0) / stats.total) * 100)
                : 0}
              % of total
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 px-3 pt-3">
            <CardTitle className="text-xs font-medium text-green-600">
              Areas
            </CardTitle>
            <MapPin className="h-3.5 w-3.5 text-green-600" />
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="text-xl font-bold text-green-700">
              {areas?.length || 0}
            </div>
            <p className="text-xs text-green-600 mt-0.5">Geographic coverage</p>
          </CardContent>
        </Card>

        {!isTrainee && (
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 px-3 pt-3">
              <CardTitle className="text-xs font-medium text-purple-600">
                Contacts
              </CardTitle>
              <Users className="h-3.5 w-3.5 text-purple-600" />
            </CardHeader>
            <CardContent className="px-3 pb-3">
              <div className="text-xl font-bold text-purple-700">
                {contacts?.length || 0}
              </div>
              <p className="text-xs text-purple-600 mt-0.5">
                Customer database
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* recent activity and quick actions */}
      <div
        className={`grid gap-3 ${isTrainee ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}
      >
        <Card>
          <CardHeader className="px-3 pt-3 pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Activity className="h-4 w-4" />
              Recent Properties
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="space-y-2">
              {recentProperties.length > 0 ? (
                recentProperties.map((property: any) => (
                  <div
                    key={property.baseProperty?._id}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {property.baseProperty?.property_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {property.baseProperty?.reff_id}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {property.baseProperty?.featured && (
                        <Star className="h-3 w-3 text-yellow-500 fill-current" />
                      )}
                      <Badge
                        variant={
                          property.baseProperty?.draft ? "secondary" : "default"
                        }
                        className="text-xs"
                      >
                        {property.baseProperty?.draft ? "Draft" : "Published"}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No properties found
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {!isTrainee && (
          <Card>
            <CardHeader className="px-3 pt-3 pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="h-auto p-2.5 flex flex-col items-center gap-1.5 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                  onClick={onAddProperty}
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-xs font-medium">Add Property</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-2.5 flex flex-col items-center gap-1.5 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                  onClick={onAddContact}
                >
                  <Users className="h-4 w-4" />
                  <span className="text-xs font-medium">Add Contact</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-2.5 flex flex-col items-center gap-1.5 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                  onClick={onAddArea}
                >
                  <MapPin className="h-4 w-4" />
                  <span className="text-xs font-medium">Add Area</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-2.5 flex flex-col items-center gap-1.5 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                  onClick={onGenerateReport}
                >
                  <FileText className="h-4 w-4" />
                  <span className="text-xs font-medium">Generate Report</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* featured properties */}
      {featuredProperties.length > 0 && (
        <Card>
          <CardHeader className="px-3 pt-3 pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Star className="h-4 w-4 text-yellow-500" />
              Featured Properties
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {featuredProperties.map((property: any) => (
                <Card
                  key={property.baseProperty?._id}
                  className="border-2 border-yellow-200"
                >
                  <CardContent className="p-3">
                    <div className="space-y-1.5">
                      <div className="flex items-start justify-between">
                        <h4 className="font-medium leading-none">
                          {property.baseProperty?.property_name}
                        </h4>
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {property.baseProperty?.reff_id}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {property.baseProperty?.description}
                      </p>
                      <div className="flex justify-between items-center pt-2">
                        <Badge variant="default" className="text-xs">
                          Featured
                        </Badge>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {property.baseProperty?.created_at
                              ? new Date(
                                  property.baseProperty.created_at,
                                ).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* REFF ID Gaps Analysis */}
      {!isTrainee && <GapsCard />}
    </div>
  );
}

// properties content component
function PropertiesContent() {
  return (
    <div className="w-full max-w-none flex flex-col">
      <PropertyTable />
    </div>
  );
}

// analytics content component
function AnalyticsContent() {
  return <Analytics />;
}

// contacts content component
function ContactsContent({ contacts }: { contacts: any[] }) {
  return (
    <div className="w-full max-w-none space-y-6">
      <ContactTable preloadedContacts={contacts} />
    </div>
  );
}

// areas content component
function AreasContent({
  areas,
  onAddArea,
}: {
  areas: any[];
  onAddArea: () => void;
}) {
  const [editingArea, setEditingArea] = useState<any>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [addingSubArea, setAddingSubArea] = useState<any>(null);
  const [showSubAreaForm, setShowSubAreaForm] = useState(false);
  const [deleteCandidateArea, setDeleteCandidateArea] = useState<any>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const updateArea = useMutation(api.area.update);
  const removeArea = useMutation(api.area.remove);
  const createArea = useMutation(api.area.create);
  const updateBaseProperty = useMutation(api.properties.updateBaseProperty);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // fetch properties for delete guard dialog
  const deleteGuardProperties = useQuery(
    api.area.getPropertiesInAreaIncludingSubAreas,
    deleteCandidateArea && showDeleteDialog
      ? { area_id: deleteCandidateArea._id as any }
      : ("skip" as unknown as any),
  );

  const truncate = (text: string, maxWords: number) => {
    if (!text) return "";
    const words = String(text).split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(" ") + "…";
  };

  // per-row reassignment and pagination
  const [reassignmentMap, setReassignmentMap] = useState<
    Record<string, string>
  >({});
  const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set());
  const guardPagination = usePagination({
    totalItems: Array.isArray(deleteGuardProperties)
      ? deleteGuardProperties.length
      : 0,
    itemsPerPage: 5,
  });
  const paginatedGuardProperties = Array.isArray(deleteGuardProperties)
    ? deleteGuardProperties.slice(
        guardPagination.startIndex,
        guardPagination.endIndex,
      )
    : [];
  const currentUserForDeletion = useQuery(api.users.current);

  const setRowTarget = (propertyId: string, areaId: string) => {
    setReassignmentMap((prev) => ({ ...prev, [propertyId]: areaId }));
  };

  const handleReassignOne = async (propertyId: string) => {
    const areaId = reassignmentMap[propertyId];
    if (!areaId) {
      toast.error("please select a target area first");
      return;
    }
    setSubmittingIds((prev) => new Set(prev).add(propertyId));
    try {
      const updatedBy = currentUserForDeletion
        ? currentUserForDeletion.email
        : "system";
      await updateBaseProperty({
        id: propertyId as any,
        area_id: areaId as any,
        updated_by: updatedBy,
      });
      toast.success("Property reassigned");
      setReassignmentMap((prev) => {
        const { [propertyId]: _removed, ...rest } = prev;
        return rest;
      });
    } catch (e) {
      toast.error("failed to reassign property");
    } finally {
      setSubmittingIds((prev) => {
        const next = new Set(prev);
        next.delete(propertyId);
        return next;
      });
    }
  };

  // organize areas by hierarchy and flatten for grid display
  const organizedAreas = React.useMemo(() => {
    if (!areas) return [];

    // filter out deleted areas first
    const activeAreas = areas.filter(
      (area: any) =>
        !area.deleted_status ||
        area.deleted_status === false ||
        area.deleted_status === null ||
        area.deleted_status === undefined,
    );

    const flattenHierarchy = (
      parentId: string | null,
      level: number = 0,
    ): any[] => {
      const result: any[] = [];

      activeAreas
        .filter((area: any) => area.parent_id === parentId)
        .sort((a, b) => a.area_name.localeCompare(b.area_name))
        .forEach((area) => {
          result.push({ ...area, displayLevel: level });
          result.push(...flattenHierarchy(area._id, level + 1));
        });

      return result;
    };

    // handle legacy areas that might not have parent_id or level set
    // treat areas without parent_id as root areas
    const legacyAreas = activeAreas.filter(
      (area: any) =>
        !area.parent_id ||
        area.parent_id === null ||
        area.parent_id === undefined,
    );

    // get all areas that have parent_id set (these are sub-areas)
    const hierarchicalAreas = activeAreas.filter(
      (area: any) =>
        area.parent_id &&
        area.parent_id !== null &&
        area.parent_id !== undefined,
    );

    // combine and sort all areas
    const allAreas = [...legacyAreas, ...hierarchicalAreas];

    // create a map for quick parent lookup
    const areaMap = new Map();
    allAreas.forEach((area) => areaMap.set(area._id, area));

    // assign display levels and parent info
    const result = allAreas.map((area) => {
      if (!area.parent_id) {
        // root area - find children
        const children = allAreas.filter(
          (child) => child.parent_id === area._id,
        );
        return {
          ...area,
          displayLevel: 0,
          parentName: null,
          childrenCount: children.length,
          children: children.map((child) => ({
            id: child._id,
            name: child.area_name,
            level: child.displayLevel || 1,
          })),
        };
      } else {
        // sub-area - find parent and calculate level
        let currentArea = area;
        let level = 0;
        let parentName = null;

        while (currentArea.parent_id) {
          level++;
          const parent = areaMap.get(currentArea.parent_id);
          if (parent) {
            parentName = parent.area_name;
            currentArea = parent;
          } else {
            break; // prevent infinite loop if parent not found
          }
        }

        // also find children for sub-areas
        const children = allAreas.filter(
          (child) => child.parent_id === area._id,
        );
        return {
          ...area,
          displayLevel: level,
          parentName,
          childrenCount: children.length,
          children: children.map((child) => ({
            id: child._id,
            name: child.area_name,
            level: child.displayLevel || level + 1,
          })),
        };
      }
    });

    // sort by level first, then by children count (descending), then by name
    return result.sort((a, b) => {
      if (a.displayLevel !== b.displayLevel) {
        return a.displayLevel - b.displayLevel;
      }

      // if same level, sort by children count (most children first)
      if (a.childrenCount !== b.childrenCount) {
        return b.childrenCount - a.childrenCount; // descending order
      }

      // if same children count, sort alphabetically
      return a.area_name.localeCompare(b.area_name);
    });
  }, [areas]);

  const handleEdit = (area: any) => {
    setEditingArea(area);
    setShowEditForm(true);
  };

  const handleAddSubArea = (parentArea: any) => {
    setAddingSubArea(parentArea);
    setShowSubAreaForm(true);
  };

  const handleDelete = async (areaId: string) => {
    // ---------------------------------------------------------------------------
    // open delete guard dialog; actual deletion will be performed there
    // ---------------------------------------------------------------------------
    const areaObj = (areas || []).find((a: any) => a._id === areaId);
    setDeleteCandidateArea(areaObj || { _id: areaId });
    setShowDeleteDialog(true);
  };

  const handleUpdate = async (formData: any) => {
    if (!editingArea) return;

    setIsSubmitting(true);
    try {
      await updateArea({
        id: editingArea._id as any,
        input: {
          ...formData,
          parent_id: formData.parent_id
            ? (formData.parent_id as any)
            : undefined,
        },
      });
      toast.success("Area updated successfully");
      setShowEditForm(false);
      setEditingArea(null);
    } catch (error) {
      toast.error("Failed to update area. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubArea = async (formData: any) => {
    if (!addingSubArea) return;

    setIsSubmitting(true);
    try {
      if (formData.existingArea) {
        // update existing area to set it as a sub-area
        await updateArea({
          id: formData.existingArea._id as any,
          input: {
            parent_id: addingSubArea._id as any,
          },
        });
        toast.success(
          `"${formData.existingArea.area_name}" set as sub-area successfully!`,
        );
      } else {
        // create new sub-area
        await createArea({
          input: {
            area_name: formData.area_name,
            country: addingSubArea.country,
            province: addingSubArea.province,
            parent_id: addingSubArea._id as any,
          },
        });
        toast.success("Sub-area created successfully!");
      }
      setShowSubAreaForm(false);
      setAddingSubArea(null);
    } catch (error) {
      console.error("Error in handleCreateSubArea:", error);
      toast.error("Failed to create/set sub-area. Please try again.");
      // don't close the form on error, let user try again
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAreaCard = (area: any) => (
    <Card key={area._id} className="h-fit">
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-1">
                {area.displayLevel > 0 && (
                  <div className="flex items-center">
                    {Array(area.displayLevel)
                      .fill(0)
                      .map((_, i) => (
                        <div key={i} className="w-2 h-px bg-muted mx-1" />
                      ))}
                    <div className="w-2 h-2 border-l border-b border-muted rounded-bl-sm" />
                  </div>
                )}
                <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
              </div>
              <h4 className="font-medium text-sm truncate">{area.area_name}</h4>
              <Badge
                variant="secondary"
                className="text-xs px-1.5 py-0.5 flex-shrink-0"
              >
                {area.country}
              </Badge>
              <Badge
                variant="outline"
                className="text-xs px-1.5 py-0.5 flex-shrink-0"
              >
                {area.province}
              </Badge>
              {area.displayLevel > 0 && (
                <Badge
                  variant="outline"
                  className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 border-blue-200"
                >
                  L{area.displayLevel}
                </Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                  {!area.level || area.level === "root"
                    ? "Root"
                    : area.level === "sub_area"
                      ? "Sub-area"
                      : "Sub-sub-area"}
                </Badge>
                {area.parentName && (
                  <span className="text-xs text-muted-foreground">
                    → {area.parentName}
                  </span>
                )}
                {area.childrenCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 border-green-200"
                  >
                    {area.childrenCount} child
                    {area.childrenCount === 1 ? "" : "ren"}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                {/* Add Sub-Area button for root areas and sub-areas */}
                {(!area.level ||
                  area.level === "root" ||
                  area.level === "sub_area") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddSubArea(area)}
                    className="text-blue-600 hover:text-blue-700 h-7 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3" />
                    {!area.level || area.level === "root"
                      ? "Add Sub"
                      : "Add Sub-Sub"}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(area)}
                  className="h-7 w-7 p-0"
                >
                  <Settings className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(area._id)}
                  className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Show children list if area has children */}
            {area.childrenCount > 0 && (
              <div className="mt-2 pt-2 border-t border-muted/50">
                <div className="text-xs text-muted-foreground mb-1">
                  Children areas:
                </div>
                <div className="space-y-1">
                  {area.children.map((child: any) => (
                    <div
                      key={child.id}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div className="flex items-center">
                        {Array(child.level)
                          .fill(0)
                          .map((_, i) => (
                            <div key={i} className="w-1 h-px bg-muted mx-0.5" />
                          ))}
                        <div className="w-1.5 h-1.5 border-l border-b border-muted rounded-bl-sm" />
                      </div>
                      <span className="text-muted-foreground">
                        {child.name}
                      </span>
                      <Badge
                        variant="outline"
                        className="text-xs px-1 py-0 h-4"
                      >
                        L{child.level}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full max-w-none space-y-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold tracking-tight">Area Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage geographic locations and coverage areas with hierarchical
            support.
          </p>
        </div>
        <Button className="flex items-center gap-2" onClick={onAddArea}>
          <Plus className="h-4 w-4" />
          Add Area
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {organizedAreas.length > 0 ? (
          organizedAreas.map((area: any) => renderAreaCard(area))
        ) : (
          <Card className="col-span-full">
            <CardContent className="p-6 text-center">
              <Globe className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h4 className="text-base font-medium mb-1">No Areas Found</h4>
              <p className="text-sm text-muted-foreground">
                Start by adding your first coverage area.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Area Dialog */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Area</DialogTitle>
          </DialogHeader>
          <EditAreaForm
            area={editingArea}
            areas={areas}
            onSubmit={handleUpdate}
            onCancel={() => {
              setShowEditForm(false);
              setEditingArea(null);
            }}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {/* Add Sub-Area Dialog */}
      <Dialog open={showSubAreaForm} onOpenChange={setShowSubAreaForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add{" "}
              {!addingSubArea?.level || addingSubArea?.level === "root"
                ? "Sub-Area"
                : "Sub-Sub-Area"}{" "}
              to {addingSubArea?.area_name}
            </DialogTitle>
          </DialogHeader>
          <AddSubAreaForm
            parentArea={addingSubArea}
            onSubmit={handleCreateSubArea}
            onCancel={() => {
              setShowSubAreaForm(false);
              setAddingSubArea(null);
            }}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {/* delete area dialog with property guard */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete Area</DialogTitle>
          </DialogHeader>
          {deleteCandidateArea ? (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                You are attempting to delete:{" "}
                <strong>
                  {deleteCandidateArea.area_name || "Selected Area"}
                </strong>
              </div>

              {Array.isArray(deleteGuardProperties) &&
              deleteGuardProperties.length > 0 ? (
                <div className="space-y-2">
                  <div className="p-3 border rounded-md bg-yellow-50 border-yellow-200 text-yellow-800 text-sm">
                    This area cannot be deleted because the following properties
                    are still assigned to it (including its sub-areas):
                  </div>
                  <div className="space-y-3">
                    <div className="max-h-64 overflow-y-auto overflow-x-hidden border rounded-md">
                      {paginatedGuardProperties.map((p: any) => {
                        const selected = reassignmentMap[p._id as string] || "";
                        const busy = submittingIds.has(p._id as string);
                        return (
                          <div
                            key={p._id}
                            className="grid grid-cols-[1fr_auto_auto_auto] items-center px-3 py-2 border-b last:border-0 gap-3"
                          >
                            <div className="min-w-0 overflow-hidden pr-2">
                              <div className="text-sm font-medium truncate">
                                {truncate(p.property_name, 8)}
                              </div>
                              <div className="text-xs text-muted-foreground whitespace-normal break-words">
                                {p.reff_id}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 justify-self-end">
                              <select
                                value={selected}
                                onChange={(e) =>
                                  setRowTarget(p._id as string, e.target.value)
                                }
                                className="w-48 px-3 py-2 border border-input rounded-md bg-background text-sm ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              >
                                <option value="">Select area...</option>
                                {areas?.map((a: any) => (
                                  <option key={a._id} value={a._id}>
                                    {a.area_name} ({a.province}, {a.country})
                                  </option>
                                ))}
                              </select>
                              <Button
                                type="button"
                                onClick={() =>
                                  handleReassignOne(p._id as string)
                                }
                                disabled={
                                  !reassignmentMap[p._id as string] || busy
                                }
                                className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                              >
                                {busy ? "Reassigning..." : "Reassign"}
                              </Button>
                              {/* <Badge variant="outline" className="text-xs">
                                {p.draft ? "Draft" : "Published"}
                              </Badge> */}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {guardPagination.totalPages > 1 && (
                      <div className="mt-2 space-y-2">
                        <div className="text-center text-xs text-muted-foreground">
                          Showing {guardPagination.startIndex + 1}-
                          {Math.min(
                            guardPagination.endIndex,
                            deleteGuardProperties.length,
                          )}{" "}
                          of {deleteGuardProperties.length}
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => guardPagination.goToPreviousPage()}
                            disabled={!guardPagination.hasPreviousPage}
                            className="h-8 px-3 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            type="button"
                          >
                            Previous
                          </Button>
                          {guardPagination
                            .getVisiblePageNumbers()
                            .map((page, index) =>
                              page === "ellipsis" ? (
                                <span
                                  key={index}
                                  className="px-2 text-muted-foreground"
                                >
                                  ...
                                </span>
                              ) : (
                                <Button
                                  key={index}
                                  variant={
                                    guardPagination.currentPage === page
                                      ? "default"
                                      : "outline"
                                  }
                                  size="sm"
                                  onClick={() =>
                                    guardPagination.goToPage(page as number)
                                  }
                                  className="h-8 w-8 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                                  type="button"
                                >
                                  {page}
                                </Button>
                              ),
                            )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => guardPagination.goToNextPage()}
                            disabled={!guardPagination.hasNextPage}
                            className="h-8 px-3 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            type="button"
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowDeleteDialog(false)}
                      className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 border rounded-md bg-red-50 border-red-200 text-red-800 text-sm">
                    This action cannot be undone. Are you sure you want to
                    delete this area?
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowDeleteDialog(false)}
                      className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={async () => {
                        setIsSubmitting(true);
                        try {
                          await removeArea({
                            id: deleteCandidateArea._id as any,
                          });
                          toast.success("Area deleted successfully");
                          setShowDeleteDialog(false);
                          setDeleteCandidateArea(null);
                        } catch (error) {
                          toast.error(
                            "Failed to delete area. It may have sub-areas or properties.",
                          );
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                      className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                    >
                      Delete Area
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              No area selected.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// edit area form component
function EditAreaForm({
  area,
  areas,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  area: any;
  areas: any[];
  onSubmit: (formData: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [formData, setFormData] = useState({
    area_name: area?.area_name || "",
    country: area?.country || "",
    province: area?.province || "",
    parent_id: area?.parent_id || "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (area) {
      setFormData({
        area_name: area.area_name || "",
        country: area.country || "",
        province: area.province || "",
        parent_id: area.parent_id || "",
      });
    }
  }, [area]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.area_name.trim()) {
      newErrors.area_name = "Area name is required";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!formData.province.trim()) {
      newErrors.province = "Province is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit({
      area_name: formData.area_name,
      country: formData.country,
      province: formData.province,
      parent_id: formData.parent_id || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <AreaSearchInput
        value={formData.area_name}
        onChange={(value) => {
          setFormData((prev) => ({ ...prev, area_name: value }));
          if (errors.area_name)
            setErrors((prev) => ({ ...prev, area_name: "" }));
        }}
        placeholder="Enter area name"
        label="Area Name *"
        error={errors.area_name}
        excludeAreaId={area?._id}
      />

      <div>
        <Label htmlFor="edit_country">Country *</Label>
        <Input
          id="edit_country"
          value={formData.country}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, country: e.target.value }));
            if (errors.country) setErrors((prev) => ({ ...prev, country: "" }));
          }}
          placeholder="Enter country"
          className={errors.country ? "border-red-500" : ""}
        />
        {errors.country && (
          <p className="text-sm text-red-500 mt-1">{errors.country}</p>
        )}
      </div>

      <div>
        <Label htmlFor="edit_province">Province *</Label>
        <Input
          id="edit_province"
          value={formData.province}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, province: e.target.value }));
            if (errors.province)
              setErrors((prev) => ({ ...prev, province: "" }));
          }}
          placeholder="Enter province"
          className={errors.province ? "border-red-500" : ""}
        />
        {errors.province && (
          <p className="text-sm text-red-500 mt-1">{errors.province}</p>
        )}
      </div>

      <div>
        <Label htmlFor="edit_parent_id">Parent Area (Optional)</Label>
        <select
          id="edit_parent_id"
          value={formData.parent_id}
          onChange={(e) => {
            setFormData((prev) => ({
              ...prev,
              parent_id: e.target.value || undefined,
            }));
          }}
          className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="">No parent (Root area)</option>
          {areas
            ?.filter((a: any) => a._id !== area?._id) // exclude current area from parent options
            .map((a: any) => (
              <option key={a._id} value={a._id}>
                {a.area_name} ({a.province}, {a.country})
              </option>
            ))}
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          Select a parent area to create a sub-area. Leave empty for root level.
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Updating..." : "Update Area"}
        </Button>
      </div>
    </form>
  );
}

// reusable area search component
function AreaSearchInput({
  value,
  onChange,
  onSelectExisting,
  excludeAreaId,
  placeholder = "Enter area name",
  label = "Area Name",
  error,
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  onSelectExisting?: (area: any) => void;
  excludeAreaId?: string;
  placeholder?: string;
  label?: string;
  error?: string;
  className?: string;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedExistingArea, setSelectedExistingArea] = useState<any>(null);

  // get all areas for search suggestions
  const allAreas = useQuery(api.area.list, {});

  // filter existing areas that match the search query
  const matchingAreas = useMemo(() => {
    if (!searchQuery.trim() || !allAreas) return [];

    return allAreas
      .filter(
        (area: any) =>
          // only include non-deleted areas
          (!area.deleted_status ||
            area.deleted_status === false ||
            area.deleted_status === null ||
            area.deleted_status === undefined) &&
          area.area_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          (!excludeAreaId || area._id !== excludeAreaId), // exclude specific area if provided
      )
      .slice(0, 5); // limit to 5 suggestions
  }, [searchQuery, allAreas, excludeAreaId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange(value);
    setSearchQuery(value);
    setShowSuggestions(value.length > 0);
    // clear selected existing area when user types
    if (selectedExistingArea) {
      setSelectedExistingArea(null);
    }
  };

  const handleSelectExisting = (area: any) => {
    onChange(area.area_name);
    setSearchQuery(area.area_name);
    setShowSuggestions(false);
    setSelectedExistingArea(area);
    if (onSelectExisting) {
      onSelectExisting(area);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Label htmlFor="area_search">{label}</Label>
      <Input
        id="area_search"
        value={value}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(value.length > 0)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder}
        className={error ? "border-red-500" : ""}
      />

      {/* Search suggestions dropdown */}
      {showSuggestions && matchingAreas.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {matchingAreas.map((area: any) => (
            <div
              key={area._id}
              className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
              onClick={() => handleSelectExisting(area)}
            >
              <div className="font-medium">{area.area_name}</div>
              <div className="text-xs text-gray-500">
                {area.province}, {area.country}
                {area.parent_id && (
                  <span className="ml-2 text-blue-600">
                    →{" "}
                    {
                      allAreas?.find((a: any) => a._id === area.parent_id)
                        ?.area_name
                    }
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Existing area warning/selection */}
      {matchingAreas.length > 0 && value.trim() && (
        <div
          className={`mt-2 p-2 border rounded-md ${
            selectedExistingArea
              ? "bg-blue-50 border-blue-200"
              : "bg-yellow-50 border-yellow-200"
          }`}
        >
          <div className="flex items-center gap-2 text-sm">
            {selectedExistingArea ? (
              <>
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <span className="text-blue-800">
                  <strong>Selected:</strong> "{selectedExistingArea.area_name}"
                  in {selectedExistingArea.province},{" "}
                  {selectedExistingArea.country}
                </span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-yellow-800">
                  <strong>Area already exists:</strong> "
                  {matchingAreas[0].area_name}" in {matchingAreas[0].province},{" "}
                  {matchingAreas[0].country}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// add sub-area form component
function AddSubAreaForm({
  parentArea,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  parentArea: any;
  onSubmit: (formData: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [formData, setFormData] = useState({
    area_name: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedExistingArea, setSelectedExistingArea] = useState<any>(null);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.area_name.trim()) {
      newErrors.area_name = "Sub-area name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // if an existing area is selected, pass it along with the form data
    if (selectedExistingArea) {
      onSubmit({ ...formData, existingArea: selectedExistingArea });
    } else {
      onSubmit(formData);
    }
  };

  // safety check - don't render form if no parent area
  if (!parentArea) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>Parent area not found. Please try again.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground mb-2">
          <strong>Parent Area:</strong> {parentArea.area_name}
        </p>
        <p className="text-sm text-muted-foreground">
          <strong>Location:</strong> {parentArea.province}, {parentArea.country}
        </p>
      </div>

      <AreaSearchInput
        value={formData.area_name}
        onChange={(value) => {
          setFormData((prev) => ({ ...prev, area_name: value }));
          if (errors.area_name)
            setErrors((prev) => ({ ...prev, area_name: "" }));
        }}
        onSelectExisting={setSelectedExistingArea}
        excludeAreaId={parentArea._id}
        placeholder="Enter sub-area name"
        label="Sub-Area Name *"
        error={errors.area_name}
      />

      <p className="text-xs text-muted-foreground mt-1">
        {selectedExistingArea ? (
          <>
            This will set <strong>"{selectedExistingArea.area_name}"</strong> as
            a{" "}
            {!parentArea.level || parentArea.level === "root"
              ? "sub-area"
              : "sub-sub-area"}{" "}
            under {parentArea.area_name}
          </>
        ) : (
          <>
            This will create a{" "}
            {!parentArea.level || parentArea.level === "root"
              ? "sub-area"
              : "sub-sub-area"}{" "}
            under {parentArea.area_name}
          </>
        )}
      </p>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? selectedExistingArea
              ? "Setting..."
              : "Creating..."
            : selectedExistingArea
              ? "Set As Sub-Area"
              : "Create Sub-Area"}
        </Button>
      </div>
    </form>
  );
}

function AgentProfileContent() {
  const { isAuthenticated } = useConvexAuth();
  const [isEditing, setIsEditing] = useState(false);

  const currentProfile = useQuery(
    api.agents.getCurrentUserProfile,
    isAuthenticated ? {} : "skip",
  );

  const createProfile = useMutation(api.agents.createAgentProfile);
  const updateProfile = useMutation(api.agents.updateAgentProfile);
  const updateProfileImage = useMutation(api.agents.updateProfileImage);
  const updateBannerImage = useMutation(api.agents.updateBannerImage);

  const [formData, setFormData] = useState({
    slug: "",
    display_name: "",
    bio: "",
    introduction: "",
    phone: "",
    email: "",
    whatsapp: "",
    instagram: "",
    linkedin: "",
    languages: [] as string[],
    specialties: [] as string[],
    years_experience: 0,
  });

  const [profileImage, setProfileImage] = useState<{
    key: string;
    filename: string;
    contentType?: string;
    size?: number;
    blurhash?: string;
  } | null>(null);

  const [bannerImage, setBannerImage] = useState<{
    key: string;
    filename: string;
    contentType?: string;
    size?: number;
    blurhash?: string;
  } | null>(null);

  // populate form when profile loads or when starting to edit
  React.useEffect(() => {
    if (currentProfile && (isEditing || !currentProfile)) {
      setFormData({
        slug: currentProfile.slug || "",
        display_name: currentProfile.display_name || "",
        bio: currentProfile.bio || "",
        introduction: currentProfile.introduction || "",
        phone: currentProfile.phone || "",
        email: currentProfile.email || "",
        whatsapp: currentProfile.whatsapp || "",
        instagram: currentProfile.instagram || "",
        linkedin: currentProfile.linkedin || "",
        languages: currentProfile.languages || [],
        specialties: currentProfile.specialties || [],
        years_experience: currentProfile.years_experience || 0,
      });
      setProfileImage(currentProfile.profile_image || null);
      setBannerImage(currentProfile.banner_image || null);
    }
  }, [currentProfile, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (currentProfile) {
        // update existing profile
        await updateProfile({
          profileId: currentProfile._id,
          ...formData,
        });
        toast.success("Profile updated successfully");
      } else {
        // create new profile
        await createProfile(formData);
        toast.success("Profile created successfully");
      }
      setIsEditing(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save profile",
      );
    }
  };

  const handlePublishToggle = async () => {
    if (!currentProfile) return;

    try {
      await updateProfile({
        profileId: currentProfile._id,
        published: !currentProfile.published,
      });
      toast.success(
        currentProfile.published ? "Profile unpublished" : "Profile published",
      );
    } catch (error) {
      toast.error("Failed to update publish status");
    }
  };

  const handleProfileImageUpdate = async (image: typeof profileImage) => {
    if (!currentProfile) return;

    try {
      if (image) {
        await updateProfileImage({
          profileId: currentProfile._id,
          image: {
            key: image.key,
            filename: image.filename,
            contentType: image.contentType,
            size: image.size,
            blurhash: image.blurhash,
          },
        });
        toast.success("Profile image updated successfully");
      }
      setProfileImage(image);
    } catch (error) {
      toast.error("Failed to update profile image");
    }
  };

  const handleBannerImageUpdate = async (image: typeof bannerImage) => {
    if (!currentProfile) return;

    try {
      if (image) {
        await updateBannerImage({
          profileId: currentProfile._id,
          image: {
            key: image.key,
            filename: image.filename,
            contentType: image.contentType,
            size: image.size,
            blurhash: image.blurhash,
          },
        });
        toast.success("Banner image updated successfully");
      }
      setBannerImage(image);
    } catch (error) {
      toast.error("Failed to update banner image");
    }
  };

  const addLanguage = (language: string) => {
    if (language && !formData.languages.includes(language)) {
      setFormData((prev) => ({
        ...prev,
        languages: [...prev.languages, language],
      }));
    }
  };

  const removeLanguage = (language: string) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((l) => l !== language),
    }));
  };

  const addSpecialty = (specialty: string) => {
    if (specialty && !formData.specialties.includes(specialty)) {
      setFormData((prev) => ({
        ...prev,
        specialties: [...prev.specialties, specialty],
      }));
    }
  };

  const removeSpecialty = (specialty: string) => {
    setFormData((prev) => ({
      ...prev,
      specialties: prev.specialties.filter((s) => s !== specialty),
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6 overflow-y-auto">
        <Card>
          <CardContent className="pt-6">
            <p>Please log in to manage your agent profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 overflow-y-auto min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Agent Profile</h1>
          <p className="text-muted-foreground">
            Manage your public agent profile page
          </p>
        </div>
        <div className="flex items-center gap-2">
          {currentProfile && (
            <>
              <Badge
                variant={currentProfile.published ? "default" : "secondary"}
              >
                {currentProfile.published ? "Published" : "Draft"}
              </Badge>
              <Button variant="outline" onClick={handlePublishToggle}>
                {currentProfile.published ? "Unpublish" : "Publish"}
              </Button>
            </>
          )}
          {currentProfile && !isEditing && (
            <Button onClick={() => setIsEditing(true)}>Edit Profile</Button>
          )}
        </div>
      </div>

      {!currentProfile && !isEditing ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">No Agent Profile</h3>
                <p className="text-muted-foreground">
                  Create your agent profile to showcase your expertise to
                  clients
                </p>
              </div>
              <Button onClick={() => setIsEditing(true)}>
                Create Agent Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : isEditing ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {currentProfile ? "Edit Profile" : "Create Profile"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        display_name: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Profile URL Slug</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        slug: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, "-"),
                      }))
                    }
                    required
                    placeholder="your-name"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Your profile will be available at: /agents/{formData.slug}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="bio">Short Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      bio: e.target.value,
                    }))
                  }
                  placeholder="A brief description of yourself..."
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label htmlFor="introduction">Introduction</Label>
                <Textarea
                  id="introduction"
                  value={formData.introduction}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      introduction: e.target.value,
                    }))
                  }
                  placeholder="A longer introduction about your background and expertise..."
                  rows={5}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="whatsapp">WhatsApp</Label>
                  <Input
                    id="whatsapp"
                    value={formData.whatsapp}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        whatsapp: e.target.value,
                      }))
                    }
                    placeholder="+62..."
                  />
                </div>
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    value={formData.instagram}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        instagram: e.target.value,
                      }))
                    }
                    placeholder="@username"
                  />
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <Input
                    id="linkedin"
                    value={formData.linkedin}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        linkedin: e.target.value,
                      }))
                    }
                    placeholder="linkedin.com/in/..."
                  />
                </div>
              </div>

              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  value={formData.years_experience}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      years_experience: parseInt(e.target.value) || 0,
                    }))
                  }
                  min="0"
                />
              </div>

              <div>
                <Label>Languages</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.languages.map((lang) => (
                    <Badge
                      key={lang}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeLanguage(lang)}
                    >
                      {lang} ×
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add language..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addLanguage(e.currentTarget.value);
                        e.currentTarget.value = "";
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      const input = e.currentTarget
                        .previousElementSibling as HTMLInputElement;
                      if (input) {
                        addLanguage(input.value);
                        input.value = "";
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div>
                <Label>Specialties</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.specialties.map((specialty) => (
                    <Badge
                      key={specialty}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeSpecialty(specialty)}
                    >
                      {specialty} ×
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add specialty (e.g. Luxury Properties, Commercial)..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addSpecialty(e.currentTarget.value);
                        e.currentTarget.value = "";
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      const input = e.currentTarget
                        .previousElementSibling as HTMLInputElement;
                      if (input) {
                        addSpecialty(input.value);
                        input.value = "";
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit">
                  {currentProfile ? "Update Profile" : "Create Profile"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>

            {/* image uploads section */}
            {currentProfile && (
              <div className="mt-8 pt-8 border-t">
                <h3 className="text-lg font-semibold mb-6">Profile Images</h3>
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <Label className="text-base font-medium mb-3 block">
                      Profile Image
                    </Label>
                    <AgentImageUpload
                      value={profileImage}
                      onChange={handleProfileImageUpdate}
                      agentSlug={currentProfile.slug}
                      imageType="profile"
                      disabled={false}
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium mb-3 block">
                      Banner Image
                    </Label>
                    <AgentImageUpload
                      value={bannerImage}
                      onChange={handleBannerImageUpdate}
                      agentSlug={currentProfile.slug}
                      imageType="banner"
                      disabled={false}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Featured Properties Management in Edit Mode */}
            {currentProfile && (
              <div className="mt-8 pt-8 border-t">
                <h3 className="text-lg font-semibold mb-6">
                  Featured Properties
                </h3>
                <FeaturedPropertiesManager profileId={currentProfile._id} />
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Profile Preview</CardTitle>
              {currentProfile?.published && (
                <Badge variant="default">
                  Live at /agents/{currentProfile.slug}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-hidden">
            {/* Banner and Profile Section - matching public profile design */}
            <div className="relative">
              {/* Banner image */}
              <div className="relative h-64 overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 md:h-80">
                {currentProfile?.banner_image && (
                  <ImageDisplay
                    imageKey={currentProfile.banner_image.key}
                    blurhash={currentProfile.banner_image.blurhash}
                    alt={`${currentProfile.display_name} banner`}
                    className="w-full h-full"
                    objectFit="cover"
                    fill={true}
                  />
                )}
              </div>

              {/* Profile info overlay */}
              <div className="relative mx-8 -mt-16">
                <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-lg">
                  <div className="flex flex-col gap-6 md:flex-row md:items-start">
                    {/* Profile image */}
                    <div className="flex-shrink-0">
                      <div className="relative mx-auto h-32 w-32 overflow-hidden rounded-full bg-neutral-200 md:mx-0">
                        {currentProfile?.profile_image ? (
                          <ImageDisplay
                            imageKey={currentProfile.profile_image.key}
                            blurhash={currentProfile.profile_image.blurhash}
                            alt={currentProfile.display_name}
                            className="w-full h-full"
                            objectFit="cover"
                            fill={true}
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <span className="text-4xl font-medium text-neutral-500">
                              {currentProfile?.display_name
                                ?.charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Agent info */}
                    <div className="flex-1 text-center md:text-left">
                      <h1 className="mb-2 text-3xl font-bold text-neutral-900 md:text-4xl">
                        {currentProfile?.display_name}
                      </h1>
                      <p className="mb-4 text-lg leading-relaxed text-neutral-600">
                        {currentProfile?.bio}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content sections */}
            <div className="p-6 pt-8 space-y-4">
              <div>
                <h3 className="font-medium">Introduction</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {currentProfile?.introduction}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {currentProfile?.phone && (
                  <div>
                    <h3 className="font-medium">Phone</h3>
                    <p>{currentProfile.phone}</p>
                  </div>
                )}
                {currentProfile?.email && (
                  <div>
                    <h3 className="font-medium">Email</h3>
                    <p>{currentProfile.email}</p>
                  </div>
                )}
              </div>

              {currentProfile?.languages &&
                currentProfile.languages.length > 0 && (
                  <div>
                    <h3 className="font-medium">Languages</h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {currentProfile.languages.map((lang) => (
                        <Badge key={lang} variant="outline">
                          {lang}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

              {currentProfile?.specialties &&
                currentProfile.specialties.length > 0 && (
                  <div>
                    <h3 className="font-medium">Specialties</h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {currentProfile.specialties.map((specialty) => (
                        <Badge key={specialty} variant="outline">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

              {currentProfile?.years_experience &&
                currentProfile.years_experience > 0 && (
                  <div>
                    <h3 className="font-medium">Experience</h3>
                    <p>{currentProfile.years_experience} years</p>
                  </div>
                )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Featured Properties Management - Show for existing profiles */}
      {/*{currentProfile && (
        <Card>
          <CardHeader>
            <CardTitle>Featured Properties</CardTitle>
            <p className="text-muted-foreground">
              Select properties to showcase on your public profile
            </p>
          </CardHeader>
          <CardContent>
            <FeaturedPropertiesManager profileId={currentProfile._id} />
          </CardContent>
        </Card>
      )}*/}
    </div>
  );
}

function FeaturedPropertiesManager({ profileId }: { profileId: string }) {
  const [isAddingProperty, setIsAddingProperty] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const featuredProperties = useQuery(api.agents.getAgentFeaturedProperties, {
    agentId: profileId as any,
  });

  const properties = useQuery(api.properties.getAllBaseProperties, {});

  const addFeaturedProperty = useMutation(api.agents.addAgentFeaturedProperty);
  const removeFeaturedProperty = useMutation(
    api.agents.removeAgentFeaturedProperty,
  );
  const updatePropertyOrder = useMutation(
    api.agents.updateAgentFeaturedPropertyOrder,
  );

  const handleAddProperty = async (propertyId: string) => {
    try {
      await addFeaturedProperty({
        profileId: profileId as any,
        propertyId: propertyId as any,
      });
      toast.success("Property added to featured list");
      setIsAddingProperty(false);
      setSearchTerm("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to add property",
      );
    }
  };

  const handleRemoveProperty = async (propertyId: string) => {
    try {
      await removeFeaturedProperty({
        profileId: profileId as any,
        propertyId: propertyId as any,
      });
      toast.success("Property removed from featured list");
    } catch (error) {
      toast.error("Failed to remove property");
    }
  };

  // debounce search term for better performance
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredProperties =
    properties?.filter((property: any) => {
      if (!debouncedSearchTerm) return true;
      return (
        property.property_name
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()) ||
        property.reff_id
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase())
      );
    }) || [];

  const featuredPropertyIds =
    featuredProperties?.map((fp: any) => fp.baseProperty._id) || [];

  // pagination for featured properties list
  const featuredPagination = usePagination({
    totalItems: featuredProperties?.length || 0,
    itemsPerPage: 5,
  });

  const paginatedFeaturedProperties =
    featuredProperties?.slice(
      featuredPagination.startIndex,
      featuredPagination.endIndex,
    ) || [];

  // pagination for property search in dialog
  const searchPagination = usePagination({
    totalItems: filteredProperties.length,
    itemsPerPage: 20,
  });

  const paginatedProperties = filteredProperties.slice(
    searchPagination.startIndex,
    searchPagination.endIndex,
  );

  // reset page when search changes
  React.useEffect(() => {
    searchPagination.goToPage(1);
  }, [debouncedSearchTerm]);

  return (
    <div className="space-y-4">
      {/* Current Featured Properties */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium">Current Featured Properties</h4>
            <p className="text-sm text-muted-foreground">
              {featuredProperties?.length || 0} properties featured
              {featuredPagination.totalPages > 1 &&
                ` • Page ${featuredPagination.currentPage} of ${featuredPagination.totalPages}`}
            </p>
          </div>
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsAddingProperty(true);
            }}
            size="sm"
            disabled={isAddingProperty}
            data-form-type=""
            data-dashlane-ignore
            type="button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Property
          </Button>
        </div>

        {featuredProperties && featuredProperties.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedFeaturedProperties.map(
                (property: any, index: number) => (
                  <div
                    key={property.baseProperty._id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground w-8">
                          #{featuredPagination.startIndex + index + 1}
                        </span>
                        <div>
                          <h5 className="font-medium">
                            {property.baseProperty.property_name}
                          </h5>
                          <p className="text-sm text-muted-foreground">
                            {property.baseProperty.reff_id}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveProperty(property.baseProperty._id);
                      }}
                      data-form-type=""
                      data-dashlane-ignore
                      type="button"
                    >
                      Remove
                    </Button>
                  </div>
                ),
              )}
            </div>

            {/* Featured Properties Pagination */}
            {featuredPagination.totalPages > 1 && (
              <div className="mt-6 space-y-4">
                <div className="text-center text-sm text-muted-foreground">
                  Showing {featuredPagination.startIndex + 1}-
                  {Math.min(
                    featuredPagination.endIndex,
                    featuredProperties?.length || 0,
                  )}{" "}
                  of {featuredProperties?.length || 0} featured properties
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      featuredPagination.goToPreviousPage();
                    }}
                    disabled={!featuredPagination.hasPreviousPage}
                    className="h-8 px-3"
                    data-form-type=""
                    data-dashlane-ignore
                    type="button"
                  >
                    Previous
                  </Button>

                  {featuredPagination
                    .getVisiblePageNumbers()
                    .map((page, index) =>
                      page === "ellipsis" ? (
                        <span
                          key={index}
                          className="px-2 text-muted-foreground"
                        >
                          ...
                        </span>
                      ) : (
                        <Button
                          key={index}
                          variant={
                            featuredPagination.currentPage === page
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            featuredPagination.goToPage(page as number);
                          }}
                          className="h-8 w-8 p-0"
                          data-form-type=""
                          data-dashlane-ignore
                          type="button"
                        >
                          {page}
                        </Button>
                      ),
                    )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      featuredPagination.goToNextPage();
                    }}
                    disabled={!featuredPagination.hasNextPage}
                    className="h-8 px-3"
                    data-form-type=""
                    data-dashlane-ignore
                    type="button"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No featured properties yet</p>
            <p className="text-sm">
              Add properties to showcase on your profile
            </p>
          </div>
        )}
      </div>

      {/* Add Property Dialog */}
      <Dialog open={isAddingProperty} onOpenChange={setIsAddingProperty}>
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          data-form-type=""
          data-dashlane-ignore
        >
          <DialogHeader>
            <DialogTitle>Add Featured Property</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {!properties ? (
                "Loading properties..."
              ) : (
                <>
                  {properties.length} total properties available
                  {debouncedSearchTerm &&
                    filteredProperties.length !== properties.length &&
                    ` • ${filteredProperties.length} matching search`}
                </>
              )}
            </p>
          </DialogHeader>
          <div
            className="space-y-4 flex-1 overflow-hidden flex flex-col"
            data-form-type=""
            data-dashlane-ignore
          >
            <div>
              <Label htmlFor="search">Search Properties</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by name or reference ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                {searchTerm !== debouncedSearchTerm && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
              {!properties ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                    Loading properties...
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
                    {paginatedProperties.length > 0 ? (
                      paginatedProperties.map((property: any) => {
                        const isAlreadyFeatured = featuredPropertyIds.includes(
                          property._id,
                        );

                        return (
                          <div
                            key={property._id}
                            className={`flex items-center justify-between p-3 border rounded-lg ${
                              isAlreadyFeatured
                                ? "bg-muted/50 opacity-60"
                                : "hover:bg-muted/50"
                            }`}
                          >
                            <div className="flex-1">
                              <h6 className="font-medium">
                                {property.property_name}
                              </h6>
                              <p className="text-sm text-muted-foreground">
                                {property.reff_id}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              disabled={isAlreadyFeatured}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddProperty(property._id);
                              }}
                              data-form-type=""
                              data-dashlane-ignore
                              type="button"
                            >
                              {isAlreadyFeatured ? "Already Added" : "Add"}
                            </Button>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>
                          {debouncedSearchTerm
                            ? "No properties found matching your search"
                            : "No properties found"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Search Results Pagination */}
                  {searchPagination.totalPages > 1 && (
                    <div className="mt-6 space-y-4 pt-4 border-t">
                      <div className="text-center text-sm text-muted-foreground">
                        Showing {searchPagination.startIndex + 1}-
                        {Math.min(
                          searchPagination.endIndex,
                          filteredProperties.length,
                        )}{" "}
                        of {filteredProperties.length} properties
                        {debouncedSearchTerm &&
                          ` (filtered from ${properties?.length || 0} total)`}
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            searchPagination.goToPreviousPage();
                          }}
                          disabled={!searchPagination.hasPreviousPage}
                          className="h-8 px-2 sm:px-3 text-xs sm:text-sm"
                          data-form-type=""
                          data-dashlane-ignore
                          type="button"
                        >
                          <span className="hidden sm:inline">Previous</span>
                          <span className="sm:hidden">Prev</span>
                        </Button>

                        {searchPagination
                          .getVisiblePageNumbers()
                          .map((page, index) =>
                            page === "ellipsis" ? (
                              <span
                                key={index}
                                className="px-2 text-muted-foreground"
                              >
                                ...
                              </span>
                            ) : (
                              <Button
                                key={index}
                                variant={
                                  searchPagination.currentPage === page
                                    ? "default"
                                    : "outline"
                                }
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  searchPagination.goToPage(page as number);
                                }}
                                className="h-8 w-8 p-0"
                                data-form-type=""
                                data-dashlane-ignore
                                type="button"
                              >
                                {page}
                              </Button>
                            ),
                          )}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            searchPagination.goToNextPage();
                          }}
                          disabled={!searchPagination.hasNextPage}
                          className="h-8 px-2 sm:px-3 text-xs sm:text-sm"
                          data-form-type=""
                          data-dashlane-ignore
                          type="button"
                        >
                          <span className="hidden sm:inline">Next</span>
                          <span className="sm:hidden">Next</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t bg-white">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsAddingProperty(false);
                }}
                data-form-type=""
                data-dashlane-ignore
                type="button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
